import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import * as constants from '../common/constants';

import * as JspUtil from '../common/JspUtil';
import * as errorCodes from '../common/errorCodes';
import * as actions from './actionCreators';
import ReCAPTCHA from 'react-google-recaptcha';
import PagePasswordRules from './../passwordpages/PagePasswordRules';
import PasswordMeter from './../passwordpages/PasswordMeter';

import { useJspVariables, useAppContext, useLoader, useNavigation, useLoginFlowState, useDebugRender, usePageTitle } from '../hooks';
import { IceIcon, PageContainer, PageHeader, PasswordInput, TextInput, DialogTitleWithClose } from '../components';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import MuiDialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { GLOBAL_ID_TRANSITION, GLOBAL_ID_TRANSITION_CLEAR } from '../loginpages/actionTypes';

const PageRequestGlobalId = () => {
    useDebugRender('PageRequestGlobalId');
    usePageTitle('Request Global ID');
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const { panelStyle, launcher, jspVariables } = useJspVariables();
    const { getLabelFromJspVariables } = useAppContext(jspVariables);
    const { toPageForgotPassword } = useNavigation({ launcher });
    const { showLoader, hideLoader } = useLoader();

    const { transitionToGlobalIdToken } = useLoginFlowState();
    // Login can be attempted with appKey ANY; the server resolves the real app
    // (e.g. ICEDATA1) and returns it with the transition error.
    const resolvedAppKey = useSelector(state => state.userInputsReducer.appKey);
    const [appKey, setAppKeyLocal] = useState(jspVariables.jspLoginApp || '');
    const [showExistingGlobalIdPassword, setShowExistingGlobalIdPassword] = useState(false);
    const [globalIdAlreadyRegistered, setGlobalIdAlreadyRegistered] = useState(false);

    const query = location && location.query ? location.query : {};
    const [email, setEmail] = useState(query.email || '');
    const [firstName, setFirstName] = useState(query.firstName || '');
    const [lastName, setLastName] = useState(query.lastName || '');
    const [newPassword, setNewPassword] = useState('');
    const [newConfirmPassword, setNewConfirmPassword] = useState('');
    const [existingGlobalIdPassword, setExistingGlobalIdPassword] = useState('');
    const [captchaResponse, setCaptchaResponse] = useState('');
    const [alert, setAlert] = useState(null);
    const [errors, setErrors] = useState({});
    const [successDialog, setSuccessDialog] = useState(null);
    const ignoreCaptcha = jspVariables.jspDisableCaptcha === 'Y';

    const recaptchaInstance = useRef(null);

    useEffect(() => {
        if (JspUtil.isValueAvailable(window.jspGlobalId))
            setEmail(window.jspGlobalId);
        if (JspUtil.isValueAvailable(window.jspFirstName))
            setFirstName(window.jspFirstName);
        if (JspUtil.isValueAvailable(window.jspLastName))
            setLastName(window.jspLastName);
        if (jspVariables.jspLoginApp && !appKey) {
            setAppKeyLocal(jspVariables.jspLoginApp);
        }
    }, []);

    const isConfirmGlobalIdRequest = JspUtil.isValueAvailable(jspVariables.jspOtpCode);

    const validate = () => {
        const fieldErrors = {};
        if (!email) fieldErrors.email = errorCodes.MSG_EMAIL_REQUIRED();
        if (!firstName) fieldErrors.firstName = errorCodes.MSG_FIRST_NAME_REQUIRED();
        if (!lastName) fieldErrors.lastName = errorCodes.MSG_LAST_NAME_REQUIRED();
        if (isConfirmGlobalIdRequest) {
            if (!newPassword) fieldErrors.newPassword = errorCodes.MSG_PASSWORD_REQUIRED();
            if (!newConfirmPassword) fieldErrors.newConfirmPassword = errorCodes.MSG_CONFIRM_PASSWORD_REQUIRED();
            if (newPassword && newConfirmPassword && newPassword !== newConfirmPassword) fieldErrors.newConfirmPassword = errorCodes.MSG_PASSWORDS_DO_NOT_MATCH();
        }
        setErrors(fieldErrors);
        setAlert(null);
        return Object.keys(fieldErrors).length === 0;
    };

    const requestGlobalId = async () => {
        if (!validate()) return;
        resetCaptcha();
        // Prefer the app resolved by the server over what was typed at login.
        let key = resolvedAppKey || appKey;
        if (key && key === 'ANY') {
            key = 'GLOBAL';
        }
        const appUserId = (transitionToGlobalIdToken && transitionToGlobalIdToken.loginUserId) || '';
        const appPassword = (transitionToGlobalIdToken && transitionToGlobalIdToken.password) || '';
        showLoader('Requesting Global ID...');
        try {
            const resp = await actions.requestGlobalId(email.trim(), firstName, lastName,
                existingGlobalIdPassword, appUserId, key, appPassword, captchaResponse, transitionToGlobalIdToken);
            // The transition is done, so drop the whole state — leaving errTransition
            // or errNoGlobal set makes Login offer Continue / Register Global ID again.
            // Only on success: Case 1 (-32815) still needs the token to retry with the
            // Global ID password.
            dispatch({ type: GLOBAL_ID_TRANSITION, data: GLOBAL_ID_TRANSITION_CLEAR });
            setSuccessDialog({
                heading: getLabelFromJspVariables('title_request_globalid'),
                message: JspUtil.getLabelFromJspVariables('confirmation_code_sent_email', email),
                redirectUrl: resp.redirectUrl
            });
        } catch (resp) {
            if (resp.code == errorCodes.ERR_TRANSITION_TO_GLOBAL_GLOBAL_ID_PRESENT_NOT_LINKED_TO_APP_ID) {
                // Getting this again while the field is already visible means the
                // Global ID password was wrong, so the message is the only feedback.
                setAlert({ message: resp.message, severity: showExistingGlobalIdPassword ? 'error' : 'warning' });
                setShowExistingGlobalIdPassword(true);
            } else {
                setAlert({ message: resp.message || getLabelFromJspVariables('err_generic'), severity: 'error' });
            }
        } finally {
            hideLoader();
        }
    };

    const confirmGlobalId = async () => {
        if (!validate()) return;
        showLoader('Confirming Global ID...');
        try {
            await actions.confirmGlobalId(email.trim(), firstName, lastName,
                newPassword, jspVariables.jspOtpCode);
            const respData = {
                tfaEmail: email,
                app: constants.APP_KEY_GLOBAL,
                isActiveTfaEmail: constants.YES
            };
            dispatch({ type: actions.USER_GET_TFA_DETAILS_DATA, data: respData });
            // After registration, send the user through ConfirmContact (Skip/Save).
            // From there, sourcePage = TfaRegistrationDone is the next stop on either action.
            navigate(constants.ROUTE_PAGE_CONFIRM_CONTACT, {
                state: {
                    fromGlobalId: true,
                    sourcePage: constants.ROUTE_PAGE_TFA_REGISTRATION_DONE,
                    tfaEmail: email,
                    appKey: constants.APP_KEY_GLOBAL,
                    isActiveTfaEmail: constants.YES,
                }
            });
        } catch (resp) {
            if (resp.code == errorCodes.ERR_PASSWORD_EXPIRED) {
                setSuccessDialog({
                    heading: 'Confirmation Code Expired',
                    message: JspUtil.getLabelFromJspVariables('confirmation_code_expired_sent_email', email)
                });
            } else if (resp.code == errorCodes.ERR_INVALID_OLD_PASSWORD) {
                setAlert({ message: JspUtil.getLabelFromJspVariables('err_globalid_already_registered'), severity: 'error' });
                setGlobalIdAlreadyRegistered(true);
            } else {
                setAlert({ message: resp.message || getLabelFromJspVariables('err_generic'), severity: 'error' });
            }
        } finally {
            hideLoader();
        }
    };

    const resetCaptcha = () => {
        if (recaptchaInstance.current) {
            recaptchaInstance.current.reset();
        }
    };

    const onFormSubmit = (e) => {
        e.preventDefault();
        if (isConfirmGlobalIdRequest) {
            globalIdAlreadyRegistered ? toPageForgotPassword() : confirmGlobalId();
        } else {
            requestGlobalId();
        }
    };

    const clearFieldError = (field) => {
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
    };

    const backToApplicationEnabled = jspVariables.jspBackToApplicationEnabled !== 'N';

    // Login reads the transition flags straight from Redux, so anything left over
    // here would resurrect the Continue / Register Global ID prompt.
    const toPageLogin = () => {
        dispatch({ type: GLOBAL_ID_TRANSITION, data: GLOBAL_ID_TRANSITION_CLEAR });
        navigate(constants.ROUTE_PAGE_LOGIN);
    };

    return (
        <PageContainer id="pageRequestGlobalId">
            <MuiDialog open={!!successDialog} maxWidth="sm" fullWidth
                aria-labelledby="request-global-id-success-title"
                aria-describedby="request-global-id-success-description">
                <DialogTitleWithClose id="request-global-id-success-title" onClose={toPageLogin} dataAutomationId="request_global_id_success_title">
                    {successDialog?.heading}
                </DialogTitleWithClose>
                <DialogContent id="request-global-id-success-description">{successDialog?.message}</DialogContent>
                <DialogActions>
                    {successDialog?.redirectUrl && backToApplicationEnabled &&
                        <Button variant="outlined"
                            onClick={() => { window.location.href = successDialog.redirectUrl; }}
                            data-automation-id="request_global_id_back_to_app_btn">
                            {getLabelFromJspVariables('action_return_to_application')}
                        </Button>
                    }
                    <Button variant="contained"
                        onClick={toPageLogin}
                        data-automation-id="request_global_id_success_login_btn">
                        {getLabelFromJspVariables('action_login')}
                    </Button>
                </DialogActions>
            </MuiDialog>
            <Box style={panelStyle}>
                <IceIcon launcher={launcher} />
                <PageHeader dataAutomationId="request_global_id_header">{getLabelFromJspVariables('title_request_globalid')}</PageHeader>
                {alert && <Alert severity={alert.severity} sx={{ mb: 1 }} onClose={() => setAlert(null)}>{alert.message}</Alert>}
                <Box component="form" onSubmit={onFormSubmit} id="formRequestGlobalId">
                    {!isConfirmGlobalIdRequest &&
                        <Alert severity={"info"} >{getLabelFromJspVariables('globalid_login_single')}</Alert>
                    }
                    <TextInput
                        label={getLabelFromJspVariables('label_email_address')}
                        id='email_input'
                        value={email}
                        required
                        disabled={!!isConfirmGlobalIdRequest}
                        placeholder={getLabelFromJspVariables('globalid_email_is_login')}
                        error={!!errors.email}
                        helperText={errors.email || ''}
                        onChange={e => { setEmail(e.target.value); clearFieldError('email'); }}
                    />
                    <TextInput
                        label={getLabelFromJspVariables('label_first_name')}
                        id='first_name_input'
                        value={firstName}
                        required
                        error={!!errors.firstName}
                        helperText={errors.firstName || ''}
                        onChange={e => { setFirstName(e.target.value); clearFieldError('firstName'); }}
                    />
                    <TextInput
                        label={getLabelFromJspVariables('label_last_name')}
                        id='last_name_input'
                        value={lastName}
                        required
                        error={!!errors.lastName}
                        helperText={errors.lastName || ''}
                        onChange={e => { setLastName(e.target.value); clearFieldError('lastName'); }}
                    />
                    {showExistingGlobalIdPassword &&
                        <PasswordInput
                            label={getLabelFromJspVariables('label_password')}
                            autoComplete='off'
                            value={existingGlobalIdPassword}
                            id='password_input'
                            required
                            onChange={e => setExistingGlobalIdPassword(e.target.value)}
                        />
                    }
                    {isConfirmGlobalIdRequest &&
                        <>
                            <Box>
                                <PasswordInput
                                    label={getLabelFromJspVariables('label_password')}
                                    autoComplete='off'
                                    value={newPassword}
                                    id='password_input1'
                                    dataAutomationId="new_password_input"
                                    required
                                    error={!!errors.newPassword}
                                    helperText={errors.newPassword || ''}
                                    onChange={e => { setNewPassword(e.target.value); clearFieldError('newPassword'); }}
                                />
                                <PasswordMeter password={newPassword} />
                            </Box>
                            <PasswordInput
                                label={getLabelFromJspVariables('action_confirm_password')}
                                autoComplete='off'
                                value={newConfirmPassword}
                                id='password_input2'
                                dataAutomationId="new_confirm_password_input"
                                required
                                error={!!errors.newConfirmPassword}
                                helperText={errors.newConfirmPassword || ''}
                                onChange={e => { setNewConfirmPassword(e.target.value); clearFieldError('newConfirmPassword'); }}
                            />
                        </>
                    }
                    {!transitionToGlobalIdToken && !isConfirmGlobalIdRequest && !ignoreCaptcha &&
                        <Box sx={{ pt: 1 }}>
                            <ReCAPTCHA sitekey={constants.GOOGLE_RECAPTCHA_V2_SITE_KEY}
                                ref={e => { recaptchaInstance.current = e }}
                                onChange={val => setCaptchaResponse(val)}
                            />
                        </Box>
                    }
                    {isConfirmGlobalIdRequest
                        ? globalIdAlreadyRegistered
                            ? <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                <Button variant="contained" fullWidth type="submit"
                                    data-automation-id="request_global_id_forgot_password_btn">
                                    {getLabelFromJspVariables('label_forgot_password')}
                                </Button>
                            </Box>
                            : <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                <Button variant="contained" fullWidth type="submit"
                                    data-automation-id="request_global_id_complete_registration_btn">
                                    {getLabelFromJspVariables('action_complete_registration')}
                                </Button>
                            </Box>
                        : <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1 }}>
                            <Button variant="contained" fullWidth type="submit"
                                data-automation-id="request_global_id_send_confirmation_btn">
                                {getLabelFromJspVariables('send_confirmation_email')}
                            </Button>
                            {showExistingGlobalIdPassword &&
                                <Link component="button" type="button" underline="always"
                                    id="loginPageForgotPassword"
                                    onClick={() => toPageForgotPassword()}
                                    sx={{ mt: 1, display: 'inline-block' }}
                                    data-automation-id="request_global_id_forgot_password_link">
                                    {getLabelFromJspVariables('label_forgot_password')}
                                </Link>
                            }
                        </Box>
                    }
                    {isConfirmGlobalIdRequest && <PagePasswordRules />}
                </Box>
            </Box>
        </PageContainer>
    );
};

export default PageRequestGlobalId;
